import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useFaust } from "../../contexts/FaustContext";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Play, Pause, RotateCcw, SkipBack } from "lucide-react";

export function AudioInput() {
    const { audioEnvRef } = useFaust();
    const [supported, setSupported] = useState(false);
    const [devices, setDevices] = useState<{ id: string; name: string }[]>([]);
    const [selectedInput, setSelectedInput] = useState("-1");
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLooping, setIsLooping] = useState(false);
    const waveformRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<any>(null);
    const isLoopingRef = useRef(false);

    // Keep looping ref in sync
    useEffect(() => { isLoopingRef.current = isLooping; }, [isLooping]);

    // Enumerate audio devices
    useEffect(() => {
        if (!navigator.mediaDevices) return;
        const enumerate = async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch {}
            const devs = await navigator.mediaDevices.enumerateDevices();
            const inputs = devs
                .filter((d) => d.kind === "audioinput" && d.deviceId)
                .map((d) => ({ id: d.deviceId, name: d.label || d.deviceId }));
            setDevices(inputs);
            setSupported(true);
        };
        enumerate();
        navigator.mediaDevices.ondevicechange = enumerate;
    }, []);

    // Initialize WaveSurfer once the audioCtx is ready
    useEffect(() => {
        const audioEnv = audioEnvRef.current;
        if (!waveformRef.current || wavesurferRef.current || !audioEnv.audioCtx) return;

        import("wavesurfer.js").then(({ default: WaveSurfer }) => {
            if (!waveformRef.current) return;

            const ws = WaveSurfer.create({
                container: waveformRef.current,
                audioContext: audioEnv.audioCtx,
                backend: "MediaElement",
                cursorColor: "#67e8f9",
                progressColor: "#52525b",
                waveColor: "#71717a",
                height: 60,
                splitChannels: true,
            });

            ws.on("play", () => setIsPlaying(true));
            ws.on("pause", () => setIsPlaying(false));
            ws.on("finish", () => {
                if (isLoopingRef.current) {
                    ws.play();
                } else {
                    setIsPlaying(false);
                }
            });

            ws.on("ready", () => {
                // Once the audio element is created by WaveSurfer,
                // capture it as a MediaElementSource and route through gain
                connectWaveSurferToGraph(audioEnv, waveformRef.current!);
            });

            ws.load("./02-XYLO1.mp3");
            wavesurferRef.current = ws;
        });

        return () => {
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioEnvRef.current?.audioCtx]);

    /**
     * Grab the <audio> element WaveSurfer creates, make a MediaElementSource,
     * and connect it into the gain chain so it feeds the DSP.
     */
    const connectWaveSurferToGraph = useCallback(
        (audioEnv: typeof audioEnvRef.current, container: HTMLElement) => {
            if (!audioEnv.audioCtx || !audioEnv.gainInput) return;
            if (!audioEnv.inputs) audioEnv.inputs = {};

            // WaveSurfer MediaElement backend creates an <audio> tag inside the container
            const audioEl = container.querySelector("audio") as HTMLAudioElement | null;
            if (!audioEl) return;

            // Only create the source once per element (can't call createMediaElementSource twice)
            if (!audioEnv.inputs["-1"]) {
                try {
                    audioEnv.inputs["-1"] = audioEnv.audioCtx.createMediaElementSource(audioEl);
                } catch (e) {
                    // Already captured -- might happen on HMR reload
                    console.warn("MediaElementSource already exists:", e);
                    return;
                }
            }

            // Connect: mediaElementSource -> gainInput -> (DSP if running)
            const source = audioEnv.inputs["-1"];
            source.connect(audioEnv.gainInput);
            audioEnv.currentInput = "-1";
            audioEnv.inputEnabled = true;
        },
        [audioEnvRef]
    );

    const handleInputChange = useCallback(
        async (id: string) => {
            const audioEnv = audioEnvRef.current;
            if (audioEnv.currentInput === id) return;

            // Disconnect previous input
            if (audioEnv.audioCtx && audioEnv.currentInput && audioEnv.inputs) {
                const prevInput = audioEnv.inputs[audioEnv.currentInput];
                if (prevInput && audioEnv.gainInput) {
                    try { prevInput.disconnect(audioEnv.gainInput); } catch {}
                }
            }

            setSelectedInput(id);

            if (!audioEnv.audioCtx) return;
            if (!audioEnv.inputs) audioEnv.inputs = {};

            if (id === "-1") {
                // File input -- reconnect wavesurfer source
                if (audioEnv.inputs["-1"] && audioEnv.gainInput) {
                    audioEnv.inputs["-1"].connect(audioEnv.gainInput);
                }
            } else {
                // Mic/device input
                try {
                    if (!audioEnv.inputs[id]) {
                        const stream = await navigator.mediaDevices.getUserMedia({
                            audio: { deviceId: id, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
                        });
                        audioEnv.inputs[id] = audioEnv.audioCtx.createMediaStreamSource(stream);
                    }
                    if (audioEnv.gainInput) {
                        audioEnv.inputs[id].connect(audioEnv.gainInput);
                    }
                } catch (e) {
                    toast.error("Could not access audio device", { description: String(e) });
                    return;
                }
            }

            audioEnv.currentInput = id;
            audioEnv.inputEnabled = true;
        },
        [audioEnvRef]
    );

    const togglePlay = useCallback(() => {
        const ws = wavesurferRef.current;
        if (!ws?.isReady) return;
        if (ws.isPlaying()) ws.pause();
        else ws.play();
    }, []);

    const rewind = useCallback(() => {
        wavesurferRef.current?.seekTo(0);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (!file || !wavesurferRef.current?.isReady) return;

        const audioEnv = audioEnvRef.current;

        // Disconnect old source before loading new file
        if (audioEnv.inputs?.["-1"] && audioEnv.gainInput) {
            try { audioEnv.inputs["-1"].disconnect(audioEnv.gainInput); } catch {}
            delete audioEnv.inputs["-1"];
            audioEnv.inputEnabled = false;
        }

        try {
            wavesurferRef.current.load(URL.createObjectURL(file));
            // The 'ready' event will re-create the MediaElementSource and reconnect
        } catch (err) {
            toast.error("Cannot load sound file", { description: String(err) });
        }
    }, [audioEnvRef]);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">Audio Input</Label>
                {!supported && (
                    <Badge variant="outline" className="text-[10px] h-4 border-yellow-800 text-yellow-600">
                        not supported
                    </Badge>
                )}
            </div>

            <Select value={selectedInput} onValueChange={handleInputChange}>
                <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="-1">Audio File</SelectItem>
                    {devices.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                            {d.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Waveform display (shown when using audio file input) */}
            {selectedInput === "-1" && (
                <div
                    className="relative rounded overflow-hidden bg-black"
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                >
                    {/* WaveSurfer mounts canvases + audio element here */}
                    <div ref={waveformRef} className="w-full" />

                    {/* Transport controls */}
                    <div className="absolute bottom-0 left-0 flex gap-0.5 p-0.5 opacity-40 hover:opacity-100 transition-opacity z-10">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-zinc-300"
                                    onClick={rewind}
                                >
                                    <SkipBack className="h-3 w-3" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rewind</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 text-zinc-300"
                                    onClick={togglePlay}
                                >
                                    {isPlaying ? (
                                        <Pause className="h-3 w-3" />
                                    ) : (
                                        <Play className="h-3 w-3" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Play / Pause</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={isLooping ? "default" : "ghost"}
                                    size="icon"
                                    className={`h-5 w-5 ${isLooping ? "text-[#40a6ff] bg-[#0078d4]/10" : "text-[#cccccc]"}`}
                                    onClick={() => setIsLooping(!isLooping)}
                                >
                                    <RotateCcw className="h-3 w-3" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Loop</TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            )}
        </div>
    );
}
